const APP_ORIGIN = "https://lens-app-host.byteo.app";
const SEARCH_ORIGIN = "https://search-lens.byteo.app";
const RECENTS_KEY = "lenscore:recent-lenses";
const LAST_LENS_KEY = "lenscore:last-lens";

export function lensAppOrigin(locationLike = globalThis.location) {
  try {
    const url = new URL(locationLike?.href || APP_ORIGIN);
    return url.hostname === "lens-app-host.byteo.app" ? url.origin : APP_ORIGIN;
  } catch {
    return APP_ORIGIN;
  }
}

export function proxyUrl(target, locationLike = globalThis.location) {
  const proxy = new URL("/_proxy", lensAppOrigin(locationLike));
  proxy.searchParams.set("url", String(target));
  return proxy.toString();
}

export function canonicalLensUrl(lensId) {
  const id = String(lensId || "").trim();
  if (!/^\d{1,20}$/.test(id)) throw new Error("Lens does not have a valid download id.");
  return `${SEARCH_ORIGIN}/api/lens-search/lens/${id}/download`;
}

export function runtimeLensUrl(source, locationLike = globalThis.location) {
  const value = String(source || "").trim();
  const url = new URL(value);
  return url.protocol === "https:" && url.hostname === "search-lens.byteo.app" && !url.port
    ? proxyUrl(url, locationLike)
    : url.toString();
}

function absoluteUrl(value) {
  if (!value) return "";
  try { return new URL(String(value), SEARCH_ORIGIN).toString(); }
  catch { return ""; }
}

function mediaCandidates(raw) {
  const metadata = raw?.metadata && typeof raw.metadata === "object" ? raw.metadata : {};
  const source = { ...metadata, ...raw };
  const sequencePattern = source?.image_sequence?.url_pattern || source?.imageSequence?.urlPattern || "";
  const sequence = sequencePattern ? String(sequencePattern).replace("%d", "1") : "";
  const values = [
    source.previewUrl,
    source.thumbnail_media_url,
    source.thumbnailMediaUrl,
    source.thumbnail_media_poster_url,
    source.thumbnailMediaPosterUrl,
    source.standard_media_poster_url,
    source.standardMediaPosterUrl,
    sequence,
    source.iconUrl,
    source.icon_url,
    source.icon_url_alt,
    source.snapcodeUrl,
    source.snapcode_url,
  ].map(absoluteUrl).filter(Boolean);
  return [...new Set(values)];
}

export function normalizeLens(raw) {
  const metadata = raw?.metadata && typeof raw.metadata === "object" ? raw.metadata : {};
  const source = { ...metadata, ...raw };
  const lensId = String(source.lensId || source.unlockableId || source.unlockable_id || source.lens_id || "").trim();
  const mediaUrls = mediaCandidates(raw);
  const previewUrl = mediaUrls[0] || "";
  const iconUrl = absoluteUrl(source.iconUrl || source.icon_url);
  const mediaProxyUrls = mediaUrls.map((url) => proxyUrl(url));
  return {
    lensId,
    name: String(source.name || source.lens_name || "Untitled Lens"),
    creatorName: String(source.creatorName || source.user_display_name || source.user_name || "Unknown creator"),
    previewUrl,
    previewProxyUrl: mediaProxyUrls[0] || "",
    iconUrl,
    iconProxyUrl: iconUrl ? proxyUrl(iconUrl) : "",
    mediaUrls,
    mediaProxyUrls,
  };
}

export async function searchLensCatalog({ query, page = 1, pageSize = 24, signal } = {}) {
  const value = String(query || "").trim();
  if (value.length < 3) return { lenses: [], total: 0, page: 1, pageSize, totalPages: 0 };
  const target = new URL("/api/lens-search/search", SEARCH_ORIGIN);
  target.searchParams.set("q", value);
  target.searchParams.set("sort", "relevance");
  target.searchParams.set("page", String(Math.max(1, Number(page) || 1)));
  target.searchParams.set("pageSize", String(Math.max(1, Math.min(100, Number(pageSize) || 24))));
  const response = await fetch(proxyUrl(target), { credentials: "include", signal });
  if (!response.ok) throw new Error(`Lens search failed (${response.status}).`);
  const body = await response.json();
  const lenses = (Array.isArray(body?.lenses) ? body.lenses : [])
    .map(normalizeLens)
    .filter((lens) => /^\d{1,20}$/.test(lens.lensId));
  const safePageSize = Math.max(1, Number(body?.pageSize || pageSize) || 24);
  const total = Math.max(0, Number(body?.total) || 0);
  return {
    lenses,
    total,
    page: Math.max(1, Number(body?.page) || 1),
    pageSize: safePageSize,
    totalPages: total ? Math.ceil(total / safePageSize) : 0,
  };
}

export async function fetchFavorites() {
  const response = await fetch(new URL("/api/favorites", lensAppOrigin()), { credentials: "include" });
  if (!response.ok) throw new Error(`Favorites failed to load (${response.status}).`);
  const body = await response.json();
  return (Array.isArray(body?.favorites) ? body.favorites : []).map(normalizeLens);
}

export async function putFavorite(lens) {
  const normalized = normalizeLens(lens);
  const response = await fetch(new URL(`/api/favorites/${encodeURIComponent(normalized.lensId)}`, lensAppOrigin()), {
    method: "PUT",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      name: normalized.name,
      creatorName: normalized.creatorName,
      previewUrl: normalized.previewUrl,
      iconUrl: normalized.iconUrl,
    }),
  });
  if (!response.ok) throw new Error(`Favorite could not be saved (${response.status}).`);
  return normalizeLens((await response.json()).favorite);
}

export async function deleteFavorite(lensId) {
  const response = await fetch(new URL(`/api/favorites/${encodeURIComponent(lensId)}`, lensAppOrigin()), {
    method: "DELETE",
    credentials: "include",
  });
  if (!response.ok) throw new Error(`Favorite could not be removed (${response.status}).`);
  return response.json();
}

export function readRecentLenses(storage = globalThis.localStorage) {
  try {
    const value = JSON.parse(storage?.getItem?.(RECENTS_KEY) || "[]");
    return (Array.isArray(value) ? value : []).map(normalizeLens).filter((lens) => lens.lensId).slice(0, 20);
  } catch {
    return [];
  }
}

export function rememberRecentLens(lens, storage = globalThis.localStorage) {
  const normalized = normalizeLens(lens);
  const next = [normalized, ...readRecentLenses(storage).filter((item) => item.lensId !== normalized.lensId)].slice(0, 20);
  try { storage?.setItem?.(RECENTS_KEY, JSON.stringify(next)); } catch { /* optional local history */ }
  return next;
}

export function readLastLensSelection(storage = globalThis.localStorage) {
  try {
    const value = JSON.parse(storage?.getItem?.(LAST_LENS_KEY) || "null");
    const url = String(value?.url || "").trim();
    const lensId = String(value?.lensId || "").trim();
    if (!url && !/^\d{1,20}$/.test(lensId)) return null;
    return {
      kind: url ? "url" : "id",
      url,
      lensId,
      name: String(value?.name || value?.lensId || "Last lens"),
    };
  } catch {
    return null;
  }
}

export function rememberLastLensSelection(selection, storage = globalThis.localStorage) {
  const next = {
    kind: String(selection?.url || "").trim() ? "url" : "id",
    url: String(selection?.url || "").trim(),
    lensId: String(selection?.lensId || "").trim(),
    name: String(selection?.name || selection?.lensId || "Last lens"),
  };
  if (!next.url && !/^\d{1,20}$/.test(next.lensId)) throw new Error("A lens ID or URL is required.");
  try { storage?.setItem?.(LAST_LENS_KEY, JSON.stringify(next)); } catch { /* persistence is optional */ }
  return next;
}

export function queueLensSelection(lens, storage = globalThis.sessionStorage) {
  const normalized = normalizeLens(lens);
  storage?.setItem?.("lenscore:pending-lens", JSON.stringify(normalized));
  return normalized;
}

export function takeQueuedLensSelection(storage = globalThis.sessionStorage) {
  try {
    const value = storage?.getItem?.("lenscore:pending-lens");
    if (!value) return null;
    storage.removeItem("lenscore:pending-lens");
    return normalizeLens(JSON.parse(value));
  } catch {
    return null;
  }
}
